import {Http} from '../../../Data/config/Http';
import {API_ROUTES} from '../../../constants/apiRoutes';
import type {BaseResponse} from '../../../Data/types/BaseResponse';
import {unwrapResponse, handleDataSourceError} from '../../../utils/errorHelpers';

// Types for Sales - Updated to match actual API response structure
export interface SaleDTO {
  id: number;
  invoice_number: string;
  customer_name: string;
  customer_type: 'store' | 'marketer';
  marketer_name: string;
  total_amount: number;
  paid_amount: number;
  remaining_amount: number;
  issue_date: string;
  status: string;
  payment_status: string;
  payment_method: string;
  notes: string;
}

// Detailed invoice response structure
export interface DetailedInvoiceDTO {
  id: number;
  invoice_number: string;
  store: {
    id: number;
    store_name: string;
    city: string;
    region: string;
    phone_number: string;
  };
  marketer: {
    id: number;
    full_name: string;
  };
  total_amount: number;
  payment_method: string;
  status: string;
  sale_date: string;
  due_date: string;
  notes: string | null;
  invoice_pdf_url: string | null;
  payment_terms: string;
  collection_stage: string;
  days_overdue: number;
  created_at: string;
  updated_at: string;
  cash_paid: number;
  remaining_debt: number;
  payment_status: string;
  items: SaleItemDTO[];
}

// Legacy interface for old sales API (kept for backward compatibility)
export interface LegacySaleDTO {
  id: number;
  invoice_number: string;
  total_amount: number;
  payment_method: 'credit' | 'cash' | 'bank_transfer' | 'partial';
  status: 'delivered' | 'pending' | 'cancelled' | 'overdue' | 'paid';
  sale_date: string;
  due_date: string;
  notes: string | null;
  invoice_pdf_url: string | null;
  payment_terms: string;
  collection_stage: string;
  days_overdue: number;
  created_at: string;
  updated_at: string;
  items: SaleItemDTO[];
  store: {
    id: number;
    store_name: string;
    city: string;
    region: string;
    [key: string]: any;
  };
  marketer: {
    id: number;
    full_name: string;
    [key: string]: any;
  };
}

export interface SaleItemDTO {
  id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  unit: string;
  cost_price: number;
  price: number;
  serial_number?: string | null;
}

export interface SalesStatistics {
  total_sales: number;
  total_amount: number;
  total_paid: number;
  total_remaining: number;
  average_sale_amount: number;
  sales_today: number;
  sales_this_week: number;
  sales_this_month: number;
  top_customers: Array<{
    customer_name: string;
    total_amount: number;
    total_sales: number;
  }>;
}

export const SalesAPIDataSource = {
  // جلب جميع المبيعات
  getAll: async (page = 1, limit = 20, filters?: { search?: string; marketerName?: string; marketerId?: string; invoiceNumber?: string }): Promise<{ items: SaleDTO[]; pagination?: Record<string, unknown> }> => {
    try {
      const params: Record<string, string | number> = {page, limit};

      // Add filters if provided
      if (filters?.search) {
        params.search = filters.search;
      }
      if (filters?.marketerName) {
        params.marketer_name = filters.marketerName;
      }
      if (filters?.marketerId) {
        params.marketer_id = filters.marketerId;
      }
      if (filters?.invoiceNumber) {
        params.invoice_number = filters.invoiceNumber;
      }
      const response = await Http.get<BaseResponse<SaleDTO[]>>(API_ROUTES.BILLING.INVOICES, {
        params
      });
      const raw = response.data;
      const items = unwrapResponse(raw, 'حدث خطأ في جلب المبيعات');
      return {
        items: Array.isArray(items) ? items : [],
        pagination: raw.pagination ? (raw.pagination as unknown as Record<string, unknown>) : undefined
      };
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // جلب إحصائيات المبيعات
  getStatistics: async (): Promise<SalesStatistics> => {
    try {
      const response = await Http.get<BaseResponse<SalesStatistics>>(API_ROUTES.SALES.STATISTICS);
      return unwrapResponse(response.data, 'حدث خطأ في جلب إحصائيات المبيعات');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // جلب/تعديل/حذف مبيعات محددة
  getById: async (id: string): Promise<SaleDTO> => {
    try {
      const response = await Http.get<BaseResponse<SaleDTO>>(API_ROUTES.SALES.BY_ID(id));
      return unwrapResponse(response.data, 'حدث خطأ في جلب المبيعات');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // جلب تفاصيل الفاتورة بالمعرف
  getDetailedInvoiceById: async (id: string): Promise<DetailedInvoiceDTO> => {
    try {
      const response = await Http.get<BaseResponse<DetailedInvoiceDTO>>(API_ROUTES.BILLING.INVOICE_BY_ID(id));
      return unwrapResponse(response.data, 'حدث خطأ في جلب تفاصيل الفاتورة');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // جلب تفاصيل الفاتورة برقم الفاتورة
  getByInvoiceNumber: async (invoiceNumber: string): Promise<SaleDTO> => {
    try {
      const response = await Http.get<BaseResponse<SaleDTO>>(API_ROUTES.BILLING.INVOICE_BY_NUMBER(invoiceNumber));
      return unwrapResponse(response.data, 'حدث خطأ في جلب تفاصيل الفاتورة');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  update: async (id: string, saleData: Partial<SaleDTO>): Promise<SaleDTO> => {
    try {
      const response = await Http.put<BaseResponse<SaleDTO>>(API_ROUTES.SALES.BY_ID(id), saleData);
      return unwrapResponse(response.data, 'فشل في تعديل المبيعات');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      const response = await Http.delete<BaseResponse<void>>(API_ROUTES.SALES.BY_ID(id));
      unwrapResponse(response.data, 'فشل في حذف المبيعات', [200, 204]);
    } catch (error) {
      handleDataSourceError(error);
    }
  },


  recordPayment: async (paymentData: any): Promise<SaleDTO> => {
    try {
      const response = await Http.post<BaseResponse<SaleDTO>>(API_ROUTES.SALES.RECORD_PAYMENT, paymentData);
      return unwrapResponse(response.data, 'فشل في تسجيل الدفعة', [200, 201]);
    } catch (error) {
      handleDataSourceError(error);
    }
  }
};
